import React, { useState, useEffect, useRef } from 'react';
import { useMyContext } from '../../../context/MyContext';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import axios from 'axios';
import { toast } from 'react-toastify';
import Select from 'react-dropdown-select';
import '../../../css/custom-scrollbar.css';
import { b2bCreateWarehouse, Base_Url, getpincode, fetchUser } from '../../../config/config';
import {
  Building2,
  User,
  Phone,
  Mail,
  MapPin,
  Globe,
  Hash,
  Loader2,
  CheckCircle2,
  AlertCircle,
  X,
  RotateCcw,
  Sparkles,
  Package,
  Layers,
  Box,
  ChevronDown
} from "lucide-react";

// Warehouse CFT Types for B2B
const WAREHOUSE_TYPES = [
  {
    id: 'lite',
    label: 'Lite Storage',
    description: 'Standard parcels & light freight',
    icon: Package,
    color: 'from-emerald-500 to-teal-600',
    border: 'border-emerald-500/40',
    bg: 'bg-emerald-50 dark:bg-emerald-950/40',
    textColor: 'text-emerald-700 dark:text-emerald-300',
    badge: 'Standard'
  },
  {
    id: 'heavy',
    label: 'Heavy Cargo',
    description: 'Bulk shipments, pallets & heavy machinery',
    icon: Box,
    color: 'from-amber-500 to-orange-600',
    border: 'border-amber-500/40',
    bg: 'bg-amber-50 dark:bg-amber-950/40',
    textColor: 'text-amber-700 dark:text-amber-300',
    badge: 'Heavy'
  },
  {
    id: 'dense',
    label: 'Dense Storage',
    description: 'High-density vertical stacked storage',
    icon: Layers,
    color: 'from-purple-500 to-indigo-600',
    border: 'border-purple-500/40',
    bg: 'bg-purple-50 dark:bg-purple-950/40',
    textColor: 'text-purple-700 dark:text-purple-300',
    badge: 'High Density'
  },
];

const schema = z.object({
  facilityName: z
    .string()
    .min(1, "Facility name is required")
    .regex(/^[A-Za-z0-9\s\-_.,()&]+$/, "Only letters, numbers and basic symbols (-_.,()&) allowed"),
  contactPerson: z
    .string()
    .min(1, "Contact person name is required")
    .regex(/^[A-Za-z\s.]+$/, "Only letters and spaces allowed"),
  phone: z
    .string()
    .length(10, "Phone number must be exactly 10 digits")
    .regex(/^\d{10}$/, "Phone number must contain only numbers"),
  pincode: z
    .string()
    .length(6, "Pincode must be exactly 6 digits")
    .regex(/^\d{6}$/, "Pincode must contain only numbers"),
  city: z.string().min(1, "City is required (auto-fetched from pincode)"),
  state: z.string().min(1, "State is required (auto-fetched from pincode)"),
  email: z
    .string()
    .optional()
    .refine((val) => !val || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val), "Invalid email address"),
  address: z
    .string()
    .min(1, "Complete address is required")
    .regex(/^[^#\\%&;]*$/, "Address cannot contain special characters (# \\ % & ;)"),
  returnAddress: z.string().optional(),
  returnState: z.string().optional(),
  returnCity: z.string().optional(),
  returnPincode: z.string().optional()
});

const B2BAddWarehouse = ({ isOpen, onClose, onCreated }) => {
  const { isDarkMode } = useMyContext();
  const role = localStorage.getItem("role") || localStorage.getItem("user_role");
  const isAdmin = role === "admin";

  const [showReturnAddress, setShowReturnAddress] = useState(false);
  const [selectedWarehouseType, setSelectedWarehouseType] = useState('lite');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingPincode, setIsFetchingPincode] = useState(false);
  const [pincodeVerified, setPincodeVerified] = useState(false);
  const [isFetchingReturnPincode, setIsFetchingReturnPincode] = useState(false);
  const [returnPincodeVerified, setReturnPincodeVerified] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Retailer selection for admin
  const [retailerId, setRetailerId] = useState('');
  const [retailerIdData, setRetailerIdData] = useState([]);
  const [retailerError, setRetailerError] = useState('');
  const [isLoadingRetailers, setIsLoadingRetailers] = useState(false);

  const scrollRef = useRef(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    reset,
    setValue,
    setError,
    clearErrors
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      facilityName: '',
      contactPerson: '',
      phone: '',
      pincode: '',
      city: '',
      state: '',
      email: '',
      address: '',
      returnAddress: '',
      returnState: '',
      returnCity: '',
      returnPincode: ''
    }
  });

  // Fetch retailers list for admin
  const fetchRetailers = async () => {
    setIsLoadingRetailers(true);
    const token = localStorage.getItem('token');
    const cusid = localStorage.getItem('cus_id');
    try {
      const response = await fetch(Base_Url + fetchUser, {
        method: 'GET',
        headers: {
          'token': token,
          'cus_id': cusid,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data && data.data && Array.isArray(data.data)) {
        const transformedData = data.data.map(retailer => ({
          value: retailer.id,
          label: retailer.name || `Retailer #${retailer.id}`,
        }));
        setRetailerIdData(transformedData);
      }
    } catch (error) {
      console.error('Error fetching retailers:', error);
    } finally {
      setIsLoadingRetailers(false);
    }
  };

  // Reset form and fetch retailers when drawer opens
  useEffect(() => {
    if (isOpen) {
      setApiError(null);
      setPincodeVerified(false);
      setReturnPincodeVerified(false);
      setRetailerError('');
      fetchRetailers();
    }
  }, [isOpen]);

  // Handle Retailer Selection
  const handleRetailerSelect = (values) => {
    const selected = values && values.length > 0 ? values[0] : null;
    if (selected) {
      setRetailerId(selected.value);
      setRetailerError('');
      if (apiError?.message?.includes('retailer')) {
        setApiError(null);
      }
    } else {
      setRetailerId('');
    }
  };

  // Pincode auto-lookup for facility
  const fetchPincodeData = async (pin) => {
    if (!pin || pin.length !== 6) return;
    setIsFetchingPincode(true);
    setPincodeVerified(false);
    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');
    const urlgetPincode = `${Base_Url}${getpincode}?pincode=${pin}`;

    try {
      const response = await fetch(urlgetPincode, {
        method: 'GET',
        headers: {
          'token': token,
          'cus_id': cus_id,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data && data.status === "SUCCESS" && data.data && data.data.length > 0) {
        let { city, state } = data.data[0];
        city = city ? city.charAt(0).toUpperCase() + city.slice(1).toLowerCase() : '';
        state = state ? state.charAt(0).toUpperCase() + state.slice(1).toLowerCase() : '';
        setValue("city", city, { shouldValidate: true });
        setValue("state", state, { shouldValidate: true });
        setPincodeVerified(true);
        clearErrors(["city", "state", "pincode"]);
      } else {
        setValue("city", "");
        setValue("state", "");
        setPincodeVerified(false);
        setError("pincode", {
          type: "manual",
          message: data?.message || "Invalid pincode or service not available"
        });
      }
    } catch (error) {
      setPincodeVerified(false);
      setError("pincode", { type: "manual", message: "Failed to verify pincode" });
    } finally {
      setIsFetchingPincode(false);
    }
  };

  // Pincode auto-lookup for return address
  const fetchReturnPincodeData = async (pin) => {
    if (!pin || pin.length !== 6) return;
    setIsFetchingReturnPincode(true);
    setReturnPincodeVerified(false);
    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');
    const urlgetPincode = `${Base_Url}${getpincode}?pincode=${pin}`;

    try {
      const response = await fetch(urlgetPincode, {
        method: 'GET',
        headers: {
          'token': token,
          'cus_id': cus_id,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data && data.status === "SUCCESS" && data.data && data.data.length > 0) {
        let { city, state } = data.data[0];
        city = city ? city.charAt(0).toUpperCase() + city.slice(1).toLowerCase() : '';
        state = state ? state.charAt(0).toUpperCase() + state.slice(1).toLowerCase() : '';
        setValue("returnCity", city, { shouldValidate: true });
        setValue("returnState", state, { shouldValidate: true });
        setReturnPincodeVerified(true);
        clearErrors(["returnCity", "returnState", "returnPincode"]);
      } else {
        setValue("returnCity", "");
        setValue("returnState", "");
        setReturnPincodeVerified(false);
        setError("returnPincode", {
          type: "manual",
          message: data?.message || "Invalid return pincode"
        });
      }
    } catch (error) {
      setReturnPincodeVerified(false);
      setError("returnPincode", { type: "manual", message: "Failed to verify return pincode" });
    } finally {
      setIsFetchingReturnPincode(false);
    }
  };

  const handlePincodeChange = (e) => {
    const rawVal = e.target.value.replace(/\D/g, '').slice(0, 6);
    setValue("pincode", rawVal, { shouldValidate: true });
    if (rawVal.length === 6) {
      fetchPincodeData(rawVal);
    } else {
      setPincodeVerified(false);
      setValue("city", "");
      setValue("state", "");
    }
  };

  const handleReturnPincodeChange = (e) => {
    const rawVal = e.target.value.replace(/\D/g, '').slice(0, 6);
    setValue("returnPincode", rawVal, { shouldValidate: true });
    if (rawVal.length === 6) {
      fetchReturnPincodeData(rawVal);
    } else {
      setReturnPincodeVerified(false);
      setValue("returnCity", "");
      setValue("returnState", "");
    }
  };

  const handlePhoneChange = (e) => {
    const rawVal = e.target.value.replace(/\D/g, '').slice(0, 10);
    setValue("phone", rawVal, { shouldValidate: true });
  };

  // Client validation failure
  const onErrors = (validationErrors) => {
    setApiError({
      message: "Please fill out all required fields correctly before submitting.",
      details: Object.values(validationErrors).map(err => err?.message).filter(Boolean)
    });
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Helper to map B2B backend error keys to React Hook Form field names
  const mapBackendErrorsToFields = (backendErrors) => {
    const fieldMapping = {
      name: 'facilityName',
      facility_name: 'facilityName',
      pin_code: 'pincode',
      pincode: 'pincode',
      city: 'city',
      state: 'state',
      'address_details.contact_person': 'contactPerson',
      'address_details.phone_number': 'phone',
      'address_details.address': 'address',
      'ret_address.pin': 'returnPincode',
      'ret_address.address': 'returnAddress',
      email: 'email'
    };

    Object.entries(backendErrors).forEach(([key, val]) => {
      const formField = fieldMapping[key];
      const message = Array.isArray(val) ? val[0] : String(val);
      if (formField) {
        setError(formField, { type: 'server', message });
      }
    });
  };

  // Show retailer dropdown if admin OR if retailers list is available
  const showRetailerSelect = isAdmin || retailerIdData.length > 0;

  // Submit Handler
  const onSubmit = async (data) => {
    // Admin must select a retailer
    if (showRetailerSelect && !retailerId) {
      setRetailerError("Please select a retailer");
      setApiError({ message: "Please select a retailer to assign this warehouse." });
      if (scrollRef.current) {
        scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    setApiError(null);
    setIsSubmitting(true);

    const token = localStorage.getItem('token');
    const adminId = localStorage.getItem('cus_id');

    if (!token || !adminId) {
      toast.error("Session expired. Please log in again.");
      setIsSubmitting(false);
      return;
    }

    // Return address is optional: if left blank or toggle off, use the facility's main address & pincode
    const returnPin = (showReturnAddress && data.returnPincode && data.returnPincode.trim())
      ? data.returnPincode.trim()
      : data.pincode.trim();

    const returnAddr = (showReturnAddress && data.returnAddress && data.returnAddress.trim())
      ? data.returnAddress.trim()
      : data.address.trim();

    const payload = {
      pin_code: data.pincode.trim(),
      city: data.city.trim(),
      state: data.state.trim(),
      country: "India",
      cft_type: selectedWarehouseType || "lite",
      address_details: {
        address: data.address.trim(),
        contact_person: data.contactPerson.trim(),
        phone_number: data.phone.trim(),
      },
      name: data.facilityName.trim(),
      ret_address: {
        pin: returnPin,
        address: returnAddr,
      },
      ...(retailerId ? { retailer_id: retailerId } : {}),
    };

    try {
      const response = await axios.post(Base_Url + b2bCreateWarehouse, payload, {
        headers: {
          'token': token,
          'cus_id': adminId,
          'Content-Type': 'application/json',
        },
      });

      if (response?.data?.status === "SUCCESS") {
        toast.success(response.data.message || "B2B Warehouse created successfully!");
        reset();
        setRetailerId('');
        if (onCreated) onCreated();
        if (onClose) onClose();
      } else {
        const errorMessages = response?.data?.messages || {};
        mapBackendErrorsToFields(errorMessages);
        setApiError({
          message: response?.data?.message || "Warehouse creation failed. Please check the fields below.",
          details: Object.values(errorMessages).flat().filter(Boolean)
        });
        if (scrollRef.current) {
          scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    } catch (error) {
      console.error("B2B Warehouse creation error:", error);
      const resData = error?.response?.data;
      const errorMessages = resData?.messages || {};

      mapBackendErrorsToFields(errorMessages);

      const errorList = Object.values(errorMessages).flat().filter(Boolean);
      const topMessage =
        resData?.message ||
        (errorList.length > 0 ? errorList[0] : "An error occurred while creating the warehouse.");

      setApiError({
        message: topMessage,
        details: errorList.length > 1 ? errorList : []
      });

      toast.error(topMessage);

      if (scrollRef.current) {
        scrollRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputContainerClass = (hasError) => `
    relative flex items-center rounded-xl border transition-all duration-200
    ${
      hasError
        ? 'border-rose-500 bg-rose-50/20 dark:bg-rose-950/20 ring-2 ring-rose-500/20'
        : isDarkMode
        ? 'border-slate-700/80 bg-slate-900/60 hover:border-slate-600 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20'
        : 'border-slate-200 bg-white hover:border-slate-300 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/10'
    }
  `;

  const inputClass = `
    w-full py-2.5 pl-10 pr-3.5 text-xs bg-transparent rounded-xl outline-none transition-colors
    text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500
  `;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-slate-950/60 backdrop-blur-xs z-50 transition-opacity duration-300 ${
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[540px] md:w-[600px] z-50 shadow-2xl transition-transform duration-300 ease-out flex flex-col overflow-hidden ${
          isDarkMode ? 'bg-slate-900 text-white' : 'bg-slate-50 text-slate-800'
        } ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      >
        {/* Drawer Header */}
        <div
          className={`shrink-0 px-5 py-4 border-b flex items-center justify-between ${
            isDarkMode ? 'bg-slate-900/90 border-slate-800' : 'bg-white border-slate-200'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-indigo-600 to-purple-600 text-white flex items-center justify-center shadow-md shadow-indigo-600/30">
              <Building2 size={20} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-bold tracking-tight text-slate-900 dark:text-white">
                  Add New Pickup Location
                </h2>
                <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-400 border border-indigo-200/60 dark:border-indigo-800/60">
                  B2B Cargo
                </span>
              </div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                Configure your warehouse details, retailer assignment & CFT capacity
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable Form Body */}
        <form
          onSubmit={handleSubmit(onSubmit, onErrors)}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4 custom-scrollbar">
            {/* Top API / Validation Error Banner */}
            {apiError && (
              <div className="p-3.5 rounded-xl bg-rose-50 dark:bg-rose-950/50 border border-rose-200 dark:border-rose-900/60 text-rose-700 dark:text-rose-300 text-xs flex items-start gap-2.5 shadow-xs animate-shake">
                <AlertCircle className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="font-bold">{apiError.message}</p>
                  {apiError.details && apiError.details.length > 0 && (
                    <ul className="mt-1.5 list-disc list-inside space-y-0.5 text-[11px] text-rose-600 dark:text-rose-400/90 font-medium">
                      {apiError.details.map((msg, i) => (
                        <li key={i}>{msg}</li>
                      ))}
                    </ul>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setApiError(null)}
                  className="text-rose-400 hover:text-rose-600 dark:hover:text-rose-200 p-0.5"
                >
                  <X size={14} />
                </button>
              </div>
            )}

            {/* ================= SECTION 1: FACILITY DETAILS ================= */}
            <div
              className={`rounded-2xl border p-4 sm:p-4.5 transition-colors ${
                isDarkMode ? 'bg-slate-900/60 border-slate-800' : 'bg-white border-slate-200/90 shadow-2xs'
              }`}
            >
              <div className="flex items-center justify-between pb-3 mb-3.5 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                    <Building2 size={15} />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                      Facility Details
                    </h3>
                    <p className="text-[10.5px] text-slate-400">
                      Identify your B2B warehouse & main point of contact
                    </p>
                  </div>
                </div>
                <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 bg-indigo-50 dark:bg-indigo-950/60 px-2 py-0.5 rounded-full border border-indigo-200 dark:border-indigo-800">
                  Step 1
                </span>
              </div>

              <div className="space-y-3.5">
                {/* Admin Select Retailer & Facility Name */}
                {showRetailerSelect ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {/* Select Retailer */}
                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5 flex items-center justify-between">
                          <span>Select Retailer <span className="text-rose-500">*</span></span>
                          {isLoadingRetailers && (
                            <span className="flex items-center gap-1 text-[10px] text-indigo-500 font-normal normal-case">
                              <Loader2 className="w-3 h-3 animate-spin" /> Loading...
                            </span>
                          )}
                        </label>
                        <div className="relative">
                          <Select
                            options={retailerIdData}
                            labelField="label"
                            valueField="value"
                            searchBy="label"
                            searchable={true}
                            placeholder="Search retailer..."
                            values={retailerId ? retailerIdData.filter(opt => opt.value === retailerId) : []}
                            onChange={handleRetailerSelect}
                            color="#4f46e5"
                            style={{
                              paddingLeft: '0.85rem',
                              paddingRight: '0.85rem',
                              paddingTop: '0.45rem',
                              paddingBottom: '0.45rem',
                              borderRadius: '0.75rem',
                              fontSize: '0.75rem',
                              backgroundColor: isDarkMode ? '#0F172A' : '#ffffff',
                              borderColor: retailerError ? '#f43f5e' : (isDarkMode ? '#334155' : '#e2e8f0'),
                              color: isDarkMode ? '#ffffff' : '#0f172a',
                              boxShadow: 'none',
                            }}
                            dropdownHandleRenderer={() => (
                              <ChevronDown className="w-3.5 h-3.5 text-slate-400 pointer-events-none" />
                            )}
                            dropdownRenderer={({ props, state, methods }) => (
                              <div
                                className={`p-1.5 rounded-xl border shadow-xl max-h-56 overflow-y-auto ${
                                  isDarkMode
                                    ? 'bg-slate-900 border-slate-700 text-white'
                                    : 'bg-white border-slate-200 text-slate-900'
                                }`}
                              >
                                {methods.searchResults().length === 0 ? (
                                  <div className="py-3 text-center text-xs text-slate-400">
                                    No retailers found
                                  </div>
                                ) : (
                                  methods.searchResults().map((item) => (
                                    <div
                                      key={item.value}
                                      onClick={() => methods.addItem(item)}
                                      className={`px-3 py-2 text-xs rounded-lg cursor-pointer flex items-center justify-between transition-colors ${
                                        methods.isSelected(item)
                                          ? 'bg-indigo-50 dark:bg-indigo-950/70 text-indigo-600 dark:text-indigo-400 font-bold'
                                          : isDarkMode
                                          ? 'hover:bg-slate-800 text-slate-200'
                                          : 'hover:bg-slate-100 text-slate-800'
                                      }`}
                                    >
                                      <span>{item.label}</span>
                                      {methods.isSelected(item) && (
                                        <CheckCircle2 size={13} className="text-indigo-600 dark:text-indigo-400" />
                                      )}
                                    </div>
                                  ))
                                )}
                              </div>
                            )}
                          />
                        </div>
                        {retailerError && (
                          <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                            <AlertCircle size={11} /> {retailerError}
                          </p>
                        )}
                      </div>

                      {/* Facility Name */}
                      <div>
                        <label
                          htmlFor="facility-name"
                          className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                        >
                          Facility Name <span className="text-rose-500">*</span>
                        </label>
                        <div className={inputContainerClass(errors.facilityName)}>
                          <Building2 className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                          <input
                            id="facility-name"
                            type="text"
                            placeholder="e.g. North Delhi Hub"
                            {...register('facilityName')}
                            className={inputClass}
                          />
                        </div>
                        {errors.facilityName && (
                          <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                            <AlertCircle size={11} /> {errors.facilityName.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Contact Person & Phone Number */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {/* Contact Person */}
                      <div>
                        <label
                          htmlFor="contact-person"
                          className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                        >
                          Contact Person <span className="text-rose-500">*</span>
                        </label>
                        <div className={inputContainerClass(errors.contactPerson)}>
                          <User className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                          <input
                            id="contact-person"
                            type="text"
                            placeholder="e.g. Rahul Sharma"
                            {...register('contactPerson')}
                            className={inputClass}
                          />
                        </div>
                        {errors.contactPerson && (
                          <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                            <AlertCircle size={11} /> {errors.contactPerson.message}
                          </p>
                        )}
                      </div>

                      {/* Phone Number */}
                      <div>
                        <label
                          htmlFor="phone"
                          className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                        >
                          Phone Number <span className="text-rose-500">*</span>
                        </label>
                        <div className={inputContainerClass(errors.phone)}>
                          <Phone className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                          <input
                            id="phone"
                            type="tel"
                            placeholder="10-digit mobile number"
                            maxLength={10}
                            value={watch('phone') || ''}
                            onChange={handlePhoneChange}
                            className={inputClass}
                          />
                        </div>
                        {errors.phone && (
                          <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                            <AlertCircle size={11} /> {errors.phone.message}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Email Address */}
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                      >
                        Email Address <span className="text-[10px] font-normal text-slate-400 lowercase">(optional)</span>
                      </label>
                      <div className={inputContainerClass(errors.email)}>
                        <Mail className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                        <input
                          id="email"
                          type="email"
                          placeholder="b2bhub@company.com"
                          {...register('email')}
                          className={inputClass}
                        />
                      </div>
                      {errors.email && (
                        <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                          <AlertCircle size={11} /> {errors.email.message}
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    {/* Normal Non-Admin View */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {/* Facility Name */}
                      <div>
                        <label
                          htmlFor="facility-name"
                          className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                        >
                          Facility Name <span className="text-rose-500">*</span>
                        </label>
                        <div className={inputContainerClass(errors.facilityName)}>
                          <Building2 className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                          <input
                            id="facility-name"
                            type="text"
                            placeholder="e.g. North Delhi Hub"
                            {...register('facilityName')}
                            className={inputClass}
                          />
                        </div>
                        {errors.facilityName && (
                          <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                            <AlertCircle size={11} /> {errors.facilityName.message}
                          </p>
                        )}
                      </div>

                      {/* Contact Person */}
                      <div>
                        <label
                          htmlFor="contact-person"
                          className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                        >
                          Contact Person <span className="text-rose-500">*</span>
                        </label>
                        <div className={inputContainerClass(errors.contactPerson)}>
                          <User className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                          <input
                            id="contact-person"
                            type="text"
                            placeholder="e.g. Rahul Sharma"
                            {...register('contactPerson')}
                            className={inputClass}
                          />
                        </div>
                        {errors.contactPerson && (
                          <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                            <AlertCircle size={11} /> {errors.contactPerson.message}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                      {/* Mobile */}
                      <div>
                        <label
                          htmlFor="phone"
                          className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                        >
                          Phone Number <span className="text-rose-500">*</span>
                        </label>
                        <div className={inputContainerClass(errors.phone)}>
                          <Phone className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                          <input
                            id="phone"
                            type="tel"
                            placeholder="10-digit mobile number"
                            maxLength={10}
                            value={watch('phone') || ''}
                            onChange={handlePhoneChange}
                            className={inputClass}
                          />
                        </div>
                        {errors.phone && (
                          <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                            <AlertCircle size={11} /> {errors.phone.message}
                          </p>
                        )}
                      </div>

                      {/* Email */}
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                        >
                          Email Address <span className="text-[10px] font-normal text-slate-400 lowercase">(optional)</span>
                        </label>
                        <div className={inputContainerClass(errors.email)}>
                          <Mail className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                          <input
                            id="email"
                            type="email"
                            placeholder="b2bhub@company.com"
                            {...register('email')}
                            className={inputClass}
                          />
                        </div>
                        {errors.email && (
                          <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                            <AlertCircle size={11} /> {errors.email.message}
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* ================= SECTION 2: B2B CARGO & STORAGE TYPE (CFT) ================= */}
            <div
              className={`rounded-2xl border p-4 sm:p-4.5 transition-colors ${
                isDarkMode ? 'bg-slate-900/60 border-slate-800' : 'bg-white border-slate-200/90 shadow-2xs'
              }`}
            >
              <div className="flex items-center justify-between pb-3 mb-3.5 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-purple-50 dark:bg-purple-950/60 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                    <Layers size={15} />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                      Storage & Cargo Type (CFT)
                    </h3>
                    <p className="text-[10.5px] text-slate-400">
                      Select capacity categorization for B2B logistics rate calculation
                    </p>
                  </div>
                </div>
                <span className="text-[10px] font-bold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-950/60 px-2 py-0.5 rounded-full border border-purple-200 dark:border-purple-800">
                  Step 2
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                {WAREHOUSE_TYPES.map((type) => {
                  const Icon = type.icon;
                  const isSelected = selectedWarehouseType === type.id;
                  return (
                    <div
                      key={type.id}
                      onClick={() => setSelectedWarehouseType(type.id)}
                      className={`relative cursor-pointer rounded-xl p-3 border-2 transition-all duration-200 flex flex-col justify-between ${
                        isSelected
                          ? `border-indigo-600 dark:border-indigo-500 bg-indigo-50/50 dark:bg-indigo-950/30 shadow-xs ring-1 ring-indigo-500/30`
                          : isDarkMode
                          ? 'border-slate-800 bg-slate-800/40 hover:border-slate-700 hover:bg-slate-800/70'
                          : 'border-slate-200/90 bg-slate-50/50 hover:border-slate-300 hover:bg-slate-100/60'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div
                          className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                            isSelected
                              ? 'bg-indigo-600 text-white shadow-xs'
                              : isDarkMode
                              ? 'bg-slate-800 text-slate-400'
                              : 'bg-slate-200 text-slate-600'
                          }`}
                        >
                          <Icon size={16} />
                        </div>
                        <span
                          className={`text-[9.5px] font-bold px-1.5 py-0.5 rounded-md ${
                            isSelected
                              ? 'bg-indigo-600 text-white'
                              : isDarkMode
                              ? 'bg-slate-800 text-slate-400'
                              : 'bg-slate-200 text-slate-600'
                          }`}
                        >
                          {type.badge}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-1.5">
                          <h4 className="text-xs font-bold text-slate-900 dark:text-white">
                            {type.label}
                          </h4>
                          {isSelected && <CheckCircle2 size={13} className="text-indigo-600 dark:text-indigo-400 shrink-0" />}
                        </div>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 leading-snug">
                          {type.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* ================= SECTION 3: ADDRESS & PINCODE LOOKUP ================= */}
            <div
              className={`rounded-2xl border p-4 sm:p-4.5 transition-colors ${
                isDarkMode ? 'bg-slate-900/60 border-slate-800' : 'bg-white border-slate-200/90 shadow-2xs'
              }`}
            >
              <div className="flex items-center justify-between pb-3 mb-3.5 border-b border-slate-100 dark:border-slate-800">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-teal-50 dark:bg-teal-950/60 text-teal-600 dark:text-teal-400 flex items-center justify-center">
                    <MapPin size={15} />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                      Address & Location
                    </h3>
                    <p className="text-[10.5px] text-slate-400">
                      City & state are auto-verified via postal code
                    </p>
                  </div>
                </div>
                <span className="text-[10px] font-bold text-teal-600 dark:text-teal-400 bg-teal-50 dark:bg-teal-950/60 px-2 py-0.5 rounded-full border border-teal-200 dark:border-teal-800">
                  Step 3
                </span>
              </div>

              <div className="space-y-3.5">
                {/* 3 Columns: Pincode, City, State */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                  {/* Pincode with Auto-lookup */}
                  <div>
                    <label
                      htmlFor="pincode"
                      className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5 flex items-center justify-between"
                    >
                      <span>Pincode <span className="text-rose-500">*</span></span>
                      {isFetchingPincode && (
                        <span className="text-[10px] text-indigo-500 font-normal flex items-center gap-1 normal-case">
                          <Loader2 size={11} className="animate-spin" /> Fetching...
                        </span>
                      )}
                    </label>
                    <div className={inputContainerClass(errors.pincode)}>
                      <Hash className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                      <input
                        id="pincode"
                        type="text"
                        maxLength={6}
                        placeholder="6-digit PIN"
                        value={watch('pincode') || ''}
                        onChange={handlePincodeChange}
                        className={`${inputClass} pr-9 font-mono tracking-wider`}
                      />
                      {pincodeVerified && (
                        <CheckCircle2 className="absolute right-3 w-4 h-4 text-emerald-500 animate-in fade-in" />
                      )}
                    </div>
                    {errors.pincode && (
                      <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                        <AlertCircle size={11} /> {errors.pincode.message}
                      </p>
                    )}
                  </div>

                  {/* City */}
                  <div>
                    <label
                      htmlFor="city"
                      className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                    >
                      City <span className="text-rose-500">*</span>
                    </label>
                    <div className={inputContainerClass(errors.city)}>
                      <Building2 className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                      <input
                        id="city"
                        type="text"
                        placeholder="Auto-filled"
                        {...register('city')}
                        className={inputClass}
                      />
                    </div>
                    {errors.city && (
                      <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                        <AlertCircle size={11} /> {errors.city.message}
                      </p>
                    )}
                  </div>

                  {/* State */}
                  <div>
                    <label
                      htmlFor="state"
                      className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                    >
                      State <span className="text-rose-500">*</span>
                    </label>
                    <div className={inputContainerClass(errors.state)}>
                      <Globe className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                      <input
                        id="state"
                        type="text"
                        placeholder="Auto-filled"
                        {...register('state')}
                        className={inputClass}
                      />
                    </div>
                    {errors.state && (
                      <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                        <AlertCircle size={11} /> {errors.state.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* Country (Fixed: India) */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                  <div className="sm:col-span-1">
                    <label className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                      Country
                    </label>
                    <div className="relative flex items-center rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-800/50 py-2.5 pl-10 pr-3.5 text-xs text-slate-600 dark:text-slate-300">
                      <Globe className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                      <span>India</span>
                      <span className="ml-auto text-[10px] text-slate-400 font-semibold uppercase">Fixed</span>
                    </div>
                  </div>
                </div>

                {/* Complete Address */}
                <div>
                  <label
                    htmlFor="address"
                    className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                  >
                    Warehouse Address <span className="text-rose-500">*</span>
                  </label>
                  <div className={inputContainerClass(errors.address)}>
                    <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
                    <textarea
                      id="address"
                      rows={2}
                      placeholder="Plot / Shed no., Industrial area, Street name, Landmark"
                      {...register('address')}
                      className="w-full py-2.5 pl-10 pr-3 text-xs bg-transparent rounded-xl outline-none transition-colors resize-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
                    />
                  </div>
                  {errors.address && (
                    <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                      <AlertCircle size={11} /> {errors.address.message}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* ================= SECTION 4: DIFFERENT RETURN ADDRESS (RTO) ================= */}
            <div
              className={`rounded-2xl border p-4 sm:p-4.5 transition-colors ${
                isDarkMode ? 'bg-slate-900/60 border-slate-800' : 'bg-white border-slate-200/90 shadow-2xs'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-amber-50 dark:bg-amber-950/60 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                    <RotateCcw size={15} />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">
                      Different Return Address (RTO)
                    </h3>
                    <p className="text-[10.5px] text-slate-400">
                      Enable if non-deliverable B2B cargo returns to a different facility
                    </p>
                  </div>
                </div>

                {/* Toggle Switch */}
                <button
                  type="button"
                  onClick={() => setShowReturnAddress(!showReturnAddress)}
                  className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                    showReturnAddress ? 'bg-indigo-600' : isDarkMode ? 'bg-slate-700' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                      showReturnAddress ? 'translate-x-5' : 'translate-x-0'
                    }`}
                  />
                </button>
              </div>

              {/* Collapsible Return Fields */}
              {showReturnAddress && (
                <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 space-y-3.5 animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                    {/* Return Pincode */}
                    <div>
                      <label
                        htmlFor="return-pincode"
                        className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5 flex items-center justify-between"
                      >
                        <span>Return Pincode</span>
                        {isFetchingReturnPincode && (
                          <span className="text-[10px] text-indigo-500 font-normal flex items-center gap-1 normal-case">
                            <Loader2 size={11} className="animate-spin" /> Fetching...
                          </span>
                        )}
                      </label>
                      <div className={inputContainerClass(errors.returnPincode)}>
                        <Hash className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                        <input
                          id="return-pincode"
                          type="text"
                          maxLength={6}
                          placeholder="6-digit PIN"
                          value={watch('returnPincode') || ''}
                          onChange={handleReturnPincodeChange}
                          className={`${inputClass} pr-9 font-mono`}
                        />
                        {returnPincodeVerified && (
                          <CheckCircle2 className="absolute right-3 w-4 h-4 text-emerald-500 animate-in fade-in" />
                        )}
                      </div>
                      {errors.returnPincode && (
                        <p className="flex items-center gap-1 text-[10.5px] font-semibold text-rose-500 mt-1">
                          <AlertCircle size={11} /> {errors.returnPincode.message}
                        </p>
                      )}
                    </div>

                    {/* Return City */}
                    <div>
                      <label
                        htmlFor="return-city"
                        className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                      >
                        Return City
                      </label>
                      <div className={inputContainerClass(errors.returnCity)}>
                        <Building2 className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                        <input
                          id="return-city"
                          type="text"
                          placeholder="Auto-filled"
                          {...register('returnCity')}
                          className={inputClass}
                        />
                      </div>
                    </div>

                    {/* Return State */}
                    <div>
                      <label
                        htmlFor="return-state"
                        className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                      >
                        Return State
                      </label>
                      <div className={inputContainerClass(errors.returnState)}>
                        <Globe className="absolute left-3 w-4 h-4 text-slate-400 pointer-events-none" />
                        <input
                          id="return-state"
                          type="text"
                          placeholder="Auto-filled"
                          {...register('returnState')}
                          className={inputClass}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Return Address */}
                  <div>
                    <label
                      htmlFor="return-address"
                      className="block text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5"
                    >
                      Return Address
                    </label>
                    <div className={inputContainerClass(errors.returnAddress)}>
                      <MapPin className="absolute left-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
                      <textarea
                        id="return-address"
                        rows={2}
                        placeholder="Complete return destination address"
                        {...register('returnAddress')}
                        className="w-full py-2.5 pl-10 pr-3 text-xs bg-transparent rounded-xl outline-none transition-colors resize-none text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ================= STICKY FOOTER ACTIONS ================= */}
          <div
            className={`shrink-0 px-5 py-3.5 border-t flex items-center justify-end gap-3 backdrop-blur-md ${
              isDarkMode ? 'bg-slate-900/95 border-slate-800' : 'bg-white/95 border-slate-200'
            }`}
          >
            <button
              type="button"
              onClick={() => onClose && onClose()}
              disabled={isSubmitting}
              className="px-4 py-2 rounded-xl text-xs font-bold border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300 transition-all active:scale-95 disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2 rounded-xl text-xs font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 hover:from-indigo-500 hover:to-purple-500 text-white shadow-md shadow-indigo-600/30 hover:shadow-indigo-600/50 flex items-center gap-2 transition-all active:scale-95 disabled:opacity-60"
            >
              {isSubmitting ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  <span>Creating Warehouse...</span>
                </>
              ) : (
                <>
                  <Sparkles size={14} />
                  <span>
                    Create {selectedWarehouseType ? selectedWarehouseType.charAt(0).toUpperCase() + selectedWarehouseType.slice(1) : ''} Warehouse
                  </span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default B2BAddWarehouse;